import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import OTPInput from "react-otp-input";
import { CheckCircle2, Trash2 } from "lucide-react";
import { images } from "../../assets/images";
import { useGlobal } from "../../context/GlobalContext";

const DeleteAccount = () => {
  const { userEmailVerify, deleteAccountVerifyOtp, deleteAccount } =
    useGlobal();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const ok = await userEmailVerify({
      module: "delete-account",
      signKey: "delete_acc_sign",
      emailOverride: email,
    });
    setBusy(false);
    if (ok) setStep(2);
  };

  const handleVerifyOtp = async () => {
    setBusy(true);
    const ok = await deleteAccountVerifyOtp(otp, email);
    setBusy(false);
    if (ok) setStep(3);
  };

  const handleDelete = async () => {
    if (!confirmChecked) {
      toast.error("Please confirm before deleting your account");
      return;
    }
    setBusy(true);
    const ok = await deleteAccount(otp, email);
    setBusy(false);
    if (ok) setStep(4);
  };

  const subtitle = {
    1: "Enter your email to receive a verification code",
    2: "Enter the code sent to your email",
    3: "Confirm permanent account deletion",
    4: "Account deleted",
  }[step];

  return (
    <section className="login-section-bg">
      <div className="container-main d-flex flex-column justify-content-center">
        <div className="d-flex justify-content-center">
          <div className="auth-logo">
            <img
              src={images.pureTouchLogo}
              alt="app-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = images.appLogo;
              }}
            />
          </div>
        </div>

        <div className="card auth-card mb-5">
          <div className="text-center mb-3">
            <div
              className="mx-auto mb-2 d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: 56, height: 56, background: "#fee2e2" }}
            >
              <Trash2 size={24} color="#dc2626" />
            </div>
            <h5 className="fw-semibold text-primary mb-1">Delete Account</h5>
            <p className="text-muted fs-14 mb-0">{subtitle}</p>
          </div>

          {step < 4 && (
            <div className="pf2-steps mb-4">
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <div
                    className={`pf2-step-dot ${step >= n ? "active" : ""} ${step > n ? "done" : ""}`}
                  >
                    {n}
                  </div>
                  {n < 3 && (
                    <div
                      className={`pf2-step-line ${step > n ? "active" : ""}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {step === 1 && (
            <form className="login-form" onSubmit={handleSendOtp}>
              <div className="pf2-danger-note mb-3">
                Deleting your account is permanent. All your data will be
                removed and cannot be recovered.
              </div>
              <label className="form-label">
                Email
                {!email && <span className="text-danger"> *</span>}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control input-tags"
                placeholder="Enter your registered email"
              />
              <div className="mt-3 text-center">
                <button
                  type="submit"
                  className="btn btn-danger mt-2"
                  disabled={busy}
                >
                  {busy ? "Sending…" : "Send Verification Code"}
                </button>
              </div>
              <div className="mt-4 text-center">
                <span
                  className="text-primary cursor-pointer text-decoration-underline fw-semibold fs-14"
                  onClick={() => navigate("/login")}
                >
                  Back to Sign In
                </span>
              </div>
            </form>
          )}

          {step === 2 && (
            <div className="login-form">
              <p className="mb-1 fs-13 text-muted text-center">
                Code sent to <strong>{email}</strong>
              </p>
              <p className="fs-13 text-muted mb-3 text-center">
                Enter the 6-digit code from your email.
              </p>
              <div className="d-flex justify-content-center mb-4">
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  inputType="tel"
                  renderSeparator={<span className="mx-1" />}
                  renderInput={(props) => (
                    <input {...props} className="otp-input" />
                  )}
                />
              </div>
              <div className="d-flex flex-column align-items-center gap-2">
                <button
                  className="btn btn-danger"
                  onClick={handleVerifyOtp}
                  disabled={busy || otp.length < 6}
                >
                  {busy ? "Verifying…" : "Verify Code"}
                </button>
                <button
                  type="button"
                  className="btn btn-link text-muted fs-13 p-0"
                  onClick={handleSendOtp}
                  disabled={busy}
                >
                  Resend Code
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="login-form">
              <div className="pf2-danger-note mb-3">
                This is your final step. Once deleted, your account and all
                associated data will be permanently removed. This action cannot
                be undone.
              </div>
              <label className="pf2-confirm-check">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                <span>
                  I understand that this action is permanent and I want to
                  delete my account.
                </span>
              </label>
              <div className="text-center mt-4">
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={busy || !confirmChecked}
                >
                  {busy ? "Deleting…" : "Delete My Account"}
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="text-center">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: 60, height: 60, background: "#dcfce7" }}
              >
                <CheckCircle2 size={28} color="#16a34a" />
              </div>
              <h6 className="fw-semibold mb-1">Account Deleted</h6>
              <p className="text-muted fs-14 mb-4">
                Your account has been permanently deleted. We're sorry to see
                you go.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/login")}
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DeleteAccount;
